import React, { useMemo, useRef, useState } from "react";
import * as Icons from "react-feather";
import DataTable from "react-data-table-component";
import { ReactComponent as SortArrow } from '../../shared/img/chevron-up-down.svg';
import EnquiryFollowup from "./EnquiryFollowup";
import { logAuditEvent } from '../../utils/auditLogger';
import { formatDate } from "../../shared/CommonFunctions";

function EnquiryGrid({ enquiryList = [], onView, onEdit, onDelete, UserPermissions = {}, moduleName = 'Customer Enquiry' }) {
  const [filterText, setFilterText] = useState("");
  const [expandedRowId, setExpandedRowId] = useState(null);
  const commentRef = useRef(null);

  const rows = useMemo(() => {
    const term = filterText.toLowerCase();
    return (enquiryList || []).filter((item) => {
      return (
        (item.EnquiryNumber && item.EnquiryNumber.toLowerCase().includes(term)) ||
        (item.CustomerName && item.CustomerName.toLowerCase().includes(term)) ||
        (item.MobileNo && item.MobileNo.toLowerCase().includes(term)) ||
        (item.EnquireFor && item.EnquireFor.toLowerCase().includes(term)) ||
        (item.CreatedDate && String(item.CreatedDate).toLowerCase().includes(term))
      );
    });
  }, [enquiryList, filterText]);

  

  const columns = [
    {
      name:<div className="titlename"><span>Enquiry#</span></div> ,
      selector: row => row.EnquiryNumber,
      cell: (row) => (
        <span title={row.EnquiryNumber}>{row.EnquiryNumber}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />,
      width: '160px'
    },
    {
      name:<div className="titlename"><span>Customer Name </span></div> ,
      selector: row => row.CustomerName,
      cell: (row) => (
        <span title={row.CustomerName}>{row.CustomerName}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Mobile Number </span></div>,
      selector: row => row.MobileNo,
      cell: (row) => (
        <span title={row.MobileNo}>{row.MobileNo}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Enquire For </span></div>,
      selector: row => row.EnquireFor,
      cell: (row) => (
        <span style={{ whiteSpace: 'pre-line' }}>
          {row.EnquireFor}
        </span>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Enquiry Date</span></div>,
      selector: row => row.CreatedDate,
      cell: (row) => (
        <span title={formatDate(row.CreatedDate)}>{formatDate(row.CreatedDate)}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
  ];

  columns.push({
    name: <div className="titlename">Actions</div>,
    cell: (row) => {
      const canView = Number(UserPermissions?.ViewModule) === 1;
      const canEdit = Number(UserPermissions?.EditModule) === 1;
      const canDelete = Number(UserPermissions?.DeleteModule) === 1;
      const isSold = Boolean(row.IsSold);
      const disabledStyle = { opacity: 0.45, pointerEvents: 'none', cursor: 'not-allowed' };

      return (
        <div className="edit-delete-action">
          <span
            className="me-2 p-2"
            style={canView ? { display: 'inline-flex', cursor: 'pointer' } : { display: 'inline-flex', ...disabledStyle }}
            title={canView ? 'View' : 'No permission'}
            onClick={() => {
              if (canView) {
                try {
                  const details = JSON.stringify({ EnquiryId: row?.EnquiryId, CustomerName: row?.CustomerName });
                  logAuditEvent(moduleName, 'View Button Click', details);
                } catch (e) {}
                onView && onView(row);
              }
            }}
          >
            <Icons.Eye data-feather="eye" className="action-eye" />
          </span>
          <span
            className="me-2 p-2"
            style={canEdit && !isSold ? { display: 'inline-flex', cursor: 'pointer' } : { display: 'inline-flex', ...disabledStyle }}
            title={isSold ? 'Sold - edit disabled' : canEdit ? 'Edit' : 'No permission'}
            onClick={() => {
              if (!isSold && canEdit) {
                try {
                  const details = JSON.stringify({ EnquiryId: row?.EnquiryId, CustomerName: row?.CustomerName });
                  logAuditEvent(moduleName, 'Edit Button Click', details);
                } catch (e) {}
                onEdit && onEdit(row);
              }
            }}
          >
            <Icons.Edit data-feather="edit" className="feather-edit" />
          </span>
          <span
            className="confirm-text p-2"
            style={canDelete && !isSold ? { display: 'inline-flex', cursor: 'pointer' } : { display: 'inline-flex', ...disabledStyle }}
            title={isSold ? 'Sold - delete disabled' : canDelete ? 'Delete' : 'No permission'}
            onClick={() => {
              if (!isSold && canDelete) {
                try {
                  const details = JSON.stringify({ EnquiryId: row?.EnquiryId, CustomerName: row?.CustomerName });
                  logAuditEvent(moduleName, 'Delete Button Click', details);
                } catch (e) {}
                onDelete && onDelete(row);
              }
            }}
          >
            <Icons.Trash2 data-feather="trash-2" className="feather-trash-2" />
          </span>
        </div>
      );
    },
    center: "true",
    width: "150px"
  });

  const handleRowExpandToggled = (expanded, row) => {
    setExpandedRowId((prev) => (prev === row.EnquiryId ? null : row.EnquiryId));
  };

  const isRowExpanded = (row) => row.EnquiryId === expandedRowId;

  return (
    <>
      <div className="card table-list-card">
        <div className="card-body">
          <div className="table-top">
            <div className="search-set">
              <div className="search-input">
                <button type="button" className="btn btn-searchset">
                  <Icons.Search className="feather-search" />
                </button>
                <input
                  id="search"
                  type="text"
                  className="form-control form-control-sm custom-search-input"
                  placeholder="Search"
                  aria-label="Search Input"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DataTable
            className="table-responsive product-list"
            columns={columns}
            data={rows}
            pagination
            defaultSortField="CustomerName"
            sortIcon={<SortArrow/>}
            persistTableHead={true}
            expandableRows
            onRowExpandToggled={handleRowExpandToggled}
            expandableRowExpanded={isRowExpanded}
            expandableRowsComponent={EnquiryFollowup}
            expandableRowsComponentProps={{ ref: commentRef }}
          />
        </div>
      </div>
    </>
  );
}

export default EnquiryGrid;
